import { observable, action, computed } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import Calendar from './calendar';
import type { CandidateResponseArgs } from './candidate';
import Candidate from './candidate';
import type Event from './event';

export type AdjustmentStatus = 'adjusting' | 'fixed' | 'cancelled';
export type AdjustmentMeetingType = 'none' | 'google' | 'outlook' | 'zoom';

export type AdjustmentResponseArgs = {
  id: number;
  key?: string;
  title: string;
  place: string;
  place_address: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  duration: number;
  participants: {
    name?: string;
    calendar_id: string;
    email: string;
    provider: string | null;
    connected_calendar_id?: string;
  }[];
  candidates?: CandidateResponseArgs[];
  status?: AdjustmentStatus;
  google_meet_url?: string;
  outlook_online_meeting_url?: string;
  zoom_meeting_join_url?: string;
  meeting_type?: AdjustmentMeetingType;
  user: {
    name: string;
  };
  ref_app?: string;
  external_organization_id?: string;
  application_id?: string;
};

export type AdjustmentArgs = {
  id?: number;
  key?: string;
  title: string;
  place: string;
  placeAddress: string;
  description?: string;
  startTime?: Moment;
  endTime?: Moment;
  duration: number;
  participants: Calendar[];
  candidates: Candidate[];
  tentativeSchedulePrefix?: string;
  status?: AdjustmentStatus;
  googleMeetUrl?: string;
  outlookOnlineMeetingUrl?: string;
  zoomMeetingJoinUrl?: string;
  meetingType?: AdjustmentMeetingType;
  user?: {
    name: string;
  };
  refApp?: string;
  organizationId?: string;
  applicationId?: string;
};

export const initialAdjustmentArgs: AdjustmentArgs = {
  title: '',
  place: '',
  placeAddress: '',
  duration: 60,
  participants: [],
  candidates: [],
  meetingType: 'none'
};

export const convertAdjustmentResponseToArgs = (response: AdjustmentResponseArgs) => {
  const participants =
    response.participants?.map(
      p =>
        new Calendar({
          id: p.calendar_id,
          auth_data_id: p.connected_calendar_id || '',
          name: p.name || p.calendar_id,
          email: p.email,
          provider: p.provider
        })
    ) || [];
  const candidates =
    response.candidates?.map(
      c => new Candidate({ id: c.id, start: moment(c.start_time), end: moment(c.end_time) })
    ) || [];
  const args: AdjustmentArgs = {
    id: response.id,
    key: response.key,
    user: response.user,
    title: response.title,
    place: response.place,
    placeAddress: response.place_address,
    description: response.description,
    startTime: response.start_time ? moment(response.start_time) : undefined,
    endTime: response.end_time ? moment(response.end_time) : undefined,
    duration: response.duration,
    status: response.status,
    googleMeetUrl: response.google_meet_url,
    outlookOnlineMeetingUrl: response.outlook_online_meeting_url,
    refApp: response.ref_app,
    organizationId: response.external_organization_id,
    applicationId: response.application_id,
    zoomMeetingJoinUrl: response.zoom_meeting_join_url,
    meetingType: response.meeting_type,
    participants,
    candidates
  };
  return args;
};

export default class Adjustment {
  @observable id?: number;
  @observable key?: string;
  @observable user?: {
    name: string;
  };

  @observable title: string;
  @observable place: string;
  @observable placeAddress: string;
  @observable description?: string;
  @observable startTime?: Moment;
  @observable endTime?: Moment;
  @observable duration: number;
  @observable status?: AdjustmentStatus;
  @observable googleMeetUrl?: string;
  @observable outlookOnlineMeetingUrl?: string;
  @observable zoomMeetingJoinUrl?: string;
  @observable meetingType?: AdjustmentMeetingType;
  @observable participants: Calendar[];
  @observable candidates: Candidate[];
  @observable tentativeSchedulePrefix: string;
  @observable refApp?: string;
  @observable organizationId?: string;
  @observable applicationId?: string;

  eventlists: Event[][];

  constructor(args: AdjustmentArgs) {
    this.id = args.id;
    this.key = args.key;
    this.user = args.user;
    this.title = args.title;
    this.place = args.place;
    this.placeAddress = args.placeAddress;
    this.description = args.description;
    this.startTime = args.startTime;
    this.endTime = args.endTime;
    this.duration = args.duration;
    this.status = args.status;
    this.googleMeetUrl = args.googleMeetUrl;
    this.outlookOnlineMeetingUrl = args.outlookOnlineMeetingUrl;
    this.zoomMeetingJoinUrl = args.zoomMeetingJoinUrl;
    this.meetingType = args.meetingType;
    this.participants = args.participants;
    this.candidates = args.candidates;
    this.tentativeSchedulePrefix = args.tentativeSchedulePrefix || '【調整中】';
    this.refApp = args.refApp;
    this.organizationId = args.organizationId;
    this.applicationId = args.applicationId;
    this.eventlists = [[]];
  }

  startDate() {
    if (this.startTime) {
      return this.startTime;
    }
    return this.nearestCandidate() ? this.nearestCandidate().start : this.lastStartDate();
  }

  lastStartDate() {
    if (this.startTime) {
      return this.startTime;
    }
    if (this.candidates.length > 0) {
      return this.candidates[this.candidates.length - 1].start;
    }
    return null;
  }

  nearestCandidate() {
    return this.candidates.filter(c => c.end > moment())[0];
  }

  dateText() {
    let text = '';
    if (this.startTime && this.endTime) {
      if (this.startTime.year() !== moment().year()) {
        text = this.startTime.format('YYYY年');
      }
      text += this.startTime.format('MM月DD日(ddd) HH:mm-');
      text += this.endTime.format('HH:mm');
    } else {
      text = this.candidatesDateRangeText();
    }
    return text;
  }

  monthText() {
    return this.startDate()?.format('YYYY年 MM月');
  }

  candidatesDateRangeText() {
    if (this.candidates.length === 0) return '';
    let text = '';
    const startTimes = this.candidates.map(c => parseInt(c.start.format('x'), 10));
    const earliestDate = moment(Math.min.apply(null, startTimes));
    if (earliestDate.year() !== moment().year()) {
      text = earliestDate.format('YYYY年');
    }
    const earliestDateStr = earliestDate.format('MM月DD日(ddd)');
    const latestDateStr = moment(Math.max.apply(null, startTimes)).format('MM月DD日(ddd)');
    text += earliestDateStr === latestDateStr ? latestDateStr : `${earliestDateStr} 〜 ${latestDateStr}`;
    return text;
  }

  participantNames() {
    return this.participants.map(p => p.name).join('、');
  }

  setEventlists(evts: Event[][]) {
    this.eventlists = evts;
  }

  onlineMeetingName() {
    switch (this.meetingType) {
      case 'google':
        return 'Google Meet';
      case 'outlook':
        return 'Microsoft Teams';
      case 'zoom':
        return 'Zoom';
      default:
        return '';
    }
  }

  onlineMeetingUrl() {
    switch (this.meetingType) {
      case 'google':
        return this.googleMeetUrl;
      case 'outlook':
        return this.outlookOnlineMeetingUrl;
      case 'zoom':
        return this.zoomMeetingJoinUrl;
      default:
        return null;
    }
  }

  @computed
  get adjusting() {
    return this.status === 'adjusting';
  }

  @computed
  get fixed() {
    return this.status === 'fixed';
  }

  @computed
  get cancelled() {
    return this.status === 'cancelled';
  }

  @computed
  get isToday() {
    return this.startDate()?.isSame(moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day');
  }

  @computed
  get isPast() {
    return (this.endTime && this.endTime < moment()) || this.nearestCandidate() === undefined;
  }

  @action
  addParticipant(p: Calendar) {
    this.participants.push(p);
  }

  @action
  removeParticipant(p: Calendar) {
    const index = this.participants.indexOf(p);
    if (index > -1) {
      this.participants.splice(index, 1);
    }
  }

  @action
  removeCandidate(c: Candidate) {
    const index = this.candidates.indexOf(c);
    if (index > -1) {
      this.candidates.splice(index, 1);
    }
  }

  submitParams() {
    return {
      title: this.title,
      place: this.place,
      place_address: this.placeAddress,
      description: this.description,
      duration: this.duration,
      participants: this.participants.map(p =>
        p ? { id: p.id, provider: p.provider, auth_data_id: p.authDataId } : null
      ),
      candidates: this.candidates.map(c => {
        return {
          start: c.start.format(),
          end: c.end.format()
        };
      }),
      tentative_schedule_prefix: this.tentativeSchedulePrefix,
      ref_app: this.refApp,
      external_organization_id: this.organizationId,
      application_id: this.applicationId,
      meeting_type: this.meetingType
    };
  }

  updateParams() {
    return {
      title: this.title,
      start: this.startTime?.format(),
      end: this.endTime?.format(),
      place: this.place,
      place_address: this.placeAddress,
      participants: this.participants.map(p => ({ id: p.id, auth_data_id: p.authDataId })),
      meeting_type: this.meetingType,
      description: this.description
    };
  }
}
